<template>
  <SidebarSection title="Security">
    <template v-slot:header>
      <StatusBadgeMFA
        v-if="isUpdating"
        :enableMultiFactor="value.enableMultiFactor"
        :totpEnrolled="value.totpEnrolled"
      />
    </template>
    <div :class="$style.container">
      <ToggleSwitch
        :key="switchKey"
        :isDisabled="isDisabled"
        label="Multi-factor Authentication Not Required"
        labelOn="Multi-factor Authentication Required"
        :value="value.enableMultiFactor"
        @input="handleMFAToggle"
      />
      <Button
        v-if="isUpdating"
        ref="resetButton"
        :class="$style.noBackgroundButton"
        data-test-id="AdministratorSettings__resetTotpButton"
        hasNoBackground
        :isDisabled="!value.enableMultiFactor"
        :isLoading="loadingResetTotp"
        :text="buttonConfigResetMFA.text"
        :type="buttonConfigResetMFA.type"
        @click="handleResetMFA"
      />
      <ModalMFA
        v-if="isUpdating"
        data-test-id="AdministratorSettings__enableTotpModal"
        :item="value"
        :showModal="showEnableModal"
        @cancel="handleConfirmCancel"
        @close="handleConfirmClose"
        @save="handleConfirmContinue"
      />
      <ModalMFA
        v-if="isUpdating"
        ref="resetModal"
        data-test-id="AdministratorSettings__resetTotpModal"
        :item="value"
        :showModal="showResetModal"
        type="reset"
        @cancel="handleConfirmClose"
        @close="handleConfirmClose"
        @save="handleConfirmTotpReset"
      />
    </div>
  </SidebarSection>
</template>

<script>
import {
  Button,
  SidebarSection,
  ToggleSwitch,
} from '@jumpcloud/ui-components';
import {
  mapActions,
  mapGetters,
} from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import ModalMFA from 'client/settings/AdministratorSettings/Modals/ModalMFA.vue';
import StatusBadgeMFA from 'client/settings/AdministratorSettings/Badges/StatusBadgeMFA.vue';

export default {
  name: 'AdministratorMFASection',

  components: {
    Button,
    StatusBadgeMFA,
    ModalMFA,
    SidebarSection,
    ToggleSwitch,
  },

  props: {
    isDisabled: Boolean,
    isUpdating: Boolean,
    value: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      loadingResetTotp: false,
      switchKey: false,
      showEnableModal: false,
      showResetModal: false,
    };
  },

  computed: {
    ...mapGetters('FeatureFlagsModule', ['betaFeatures']),

    buttonConfigResetMFA() {
      const { suspended } = this.value;
      return {
        disabled: this.buttonDisabled || suspended,
        type: 'primary',
        text: 'Send TOTP Reset Email',
      };
    },
  },

  methods: {
    ...mapActions('UserCollection', ['resetTOTP']),
    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    handleConfirmCancel() {
      this.updateSwitchKey();
    },

    handleConfirmClose() {
      this.showEnableModal = false;
      this.showResetModal = false;
    },

    handleConfirmContinue() {
      this.updateItem();
    },

    async handleConfirmTotpReset() {
      const { email, _id } = this.value;
      this.loadingResetTotp = true;
      try {
        await this.resetTOTP(_id);
        this.triggerSuccessNotification(
          {
            message: `MFA Reset email sent to ${email}.`,
            title: 'Email sent',
          },
        );
      } catch (error) {
        let message;
        if (error.response && error.response.status === 412) {
          message = 'TOTP cannot be reset at this time';
        } else {
          message = `Error sending email to ${email}`;
        }
        this.triggerErrorNotification({
          title: 'Error',
          message,
        });
      } finally {
        this.loadingResetTotp = false;
      }
    },

    handleMFAToggle() {
      if (this.isUpdating) {
        this.showEnableModal = true;
      } else {
        this.updateItem();
      }
    },

    handleResetMFA() {
      this.showResetModal = true;
    },

    updateItem() {
      const { enableMultiFactor } = this.value;

      this.$emit('input', {
        ...this.value,
        enableMultiFactor: !enableMultiFactor,
      });
    },

    updateSwitchKey() {
      this.switchKey = !this.switchKey;
    },
  },
};
</script>

<style scoped module>
.noBackgroundButton {
  margin: 0 auto;
}

.container {
  display: grid;
  grid-gap: var(--jc-spacer);
}
</style>
